/**
 * Utility functions for stripping HTML tags, cleaning rich text, and creating pristine,
 * human-readable descriptions for social sharing (WhatsApp, Facebook, Twitter/X, Telegram, LinkedIn),
 * OpenGraph / Twitter Card meta tags, and SEO schema markup.
 */

export function cleanDescriptionForSocial(rawDescription?: string | null, productTitle?: string): string {
  if (!rawDescription || typeof rawDescription !== 'string') {
    return '';
  }

  let text = rawDescription;

  // 1. Convert block-level elements, lists, headings, and breaks to spaces so words don't join
  text = text
    .replace(/<\/(div|p|li|ul|ol|h[1-6]|tr|td|blockquote|section|article)>/gi, ' ')
    .replace(/<(br|hr)\s*[\/]?>/gi, ' ');

  // 2. Strip all remaining HTML tags and comments
  text = text.replace(/<!--[\s\S]*?-->/g, ' ');
  text = text.replace(/<[^>]+>/g, ' ');

  // 3. Decode common HTML entities into their actual character equivalents
  text = text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&bull;|&#8226;/gi, '•')
    .replace(/&middot;/gi, '·')
    .replace(/&#(\d+);/g, (_, code) => {
      try {
        return String.fromCharCode(Number(code));
      } catch {
        return '';
      }
    })
    .replace(/&#x([0-9a-fA-F]+);/g, (_, code) => {
      try {
        return String.fromCharCode(parseInt(code, 16));
      } catch {
        return '';
      }
    });

  // 4. Strip markdown formatting asterisks and raw backticks
  text = text.replace(/\*{1,}/g, '').replace(/`+/g, '');

  // 5. Collapse all whitespace (newlines, tabs, multiple consecutive spaces) into a single space
  text = text.replace(/[\r\n\t]+/g, ' ').replace(/\s{2,}/g, ' ').trim();

  // 6. Remove redundant boilerplate headings like "About this template", "About this design", "Overview:"
  text = text.replace(/^(?:about this template\s*[:\-\—]?\s*)+/i, '').trim();
  text = text.replace(/^(?:about this design\s*[:\-\—]?\s*)+/i, '').trim();
  text = text.replace(/^(?:template overview\s*[:\-\—]?\s*)+/i, '').trim();

  // 7. If the cleaned text immediately starts with the exact product title, format gracefully
  if (productTitle && text.toLowerCase().startsWith(productTitle.toLowerCase())) {
    const afterTitle = text.slice(productTitle.length).replace(/^[\s:\-\—]+/, '').trim();
    if (afterTitle) {
      text = `${productTitle} — ${afterTitle}`;
    }
  }

  return text.trim();
}

/**
 * Builds a pristine, professional description tailored for social media previews (WhatsApp, Facebook, etc.)
 * Ensures zero HTML tags, code snippets, or broken markup appear above or below product images.
 */
export function buildProductSocialDescription(product?: {
  title?: string;
  category?: string;
  description?: string;
  formats?: string[];
} | null): string {
  if (!product) {
    return 'RH Editography is a premium marketplace for editable PSD templates, AI templates, Vector graphics, Passports, ID Cards, Driving Licenses, and professional digital resources.';
  }

  const title = product.title || 'Digital Template';
  const category = product.category || 'Vector Design';
  const formatsList = Array.isArray(product.formats) && product.formats.length > 0
    ? product.formats.join('/')
    : 'PSD/AI';

  const clean = cleanDescriptionForSocial(product.description, title);

  // If we have a meaningful cleaned description
  if (clean && clean.length > 15) {
    // Take up to 155 characters of clean text
    const snippet = clean.length > 155 ? `${clean.slice(0, 155).trim()}...` : clean;
    return `${snippet} Download high resolution editable ${formatsList} vector template from RH Editography.`;
  }

  // Fallback description when description is minimal or absent
  return `Download high resolution editable ${title} (${category}) ${formatsList} vector template with organized smart layers and print-ready quality from RH Editography.`;
}
